// angular
import { afterNextRender, Component, effect, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { RangeInputComponent } from '@app/core/components/range-input/range-input.component';
import { SelectDropdownComponent } from '@app/core/components/select-dropdown/select-dropdown.component';
// services
import { CountriesService } from '@app/core/shared/services/countries.service';
import { LayoutService } from '@app/layout/shared/services/layout.service';
import { CategoriesService } from '@app/+categories/shared/services/categories.service';
import { CurrencyService } from '@app/core/shared/services/currency.service';
import { StorageService } from '@app/shared/services/storage.service';
// types & interfaces
import { AppliedFilter } from '@app/layout/shared/types/filter.type';
import { CategoriesType } from '@app/+categories/shared/types/categories.type';
import { II18n } from '@app/shared/interfaces/i18n.interface';
import { IDropdown } from '@app/shared/types/dropdown.interface';
import { PriceType } from '@app/+products/shared/interfaces/product.interface';
// constants & enums
import { percentageOnFiveDropDown } from '@app/shared/constants/percentage.const';
import {
  filtersMainColors,
  filtersStoneGrades,
  filtersSurfaceFinishing,
} from '@app/shared/constants/advance-filters-data.const';
import { I18n } from '@app/shared/enums/i18n.enum';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
// utils
import { removeInvalidValues } from '@app/shared/utils/objectKeysUpdater';
import { arrayToObject } from '@app/shared/utils/arrayToObject';
import { DialogRef } from '@app/core/shared/utils/dialog/dialog-ref';
import { getAvailableTranslation } from '@app/shared/utils/get-available-translation';

@Component({
  selector: 'pwj-advanced-filters',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    SelectDropdownComponent,
    ButtonComponent,
    RangeInputComponent,
    TranslateModule,
  ],
  templateUrl: './advanced-filters.component.html',
  styleUrl: './advanced-filters.component.scss',
})
export class AdvancedFiltersComponent {
  public filtersForm!: FormGroup;
  public filtersChipsList!: AppliedFilter[];
  public filtersApplied!: AppliedFilter[];
  public queryParams!: Params;
  public currentParams!: Params;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;

  // fetched product categories data
  public productCategories: CategoriesType[] = [];
  public productTypes: CategoriesType[] = [];
  public productNames: CategoriesType[] = [];

  // select options values
  public dropDownProductCategories: IDropdown[] = [];
  public dropDownProductTypes: IDropdown[] = [];
  public dropDownProductNames: IDropdown[] = [];

  // current product type & product name
  public currentProductType!: string;
  public currentProductName!: string;

  public mainColors: IDropdown[] = filtersMainColors;
  public colorPercentages: IDropdown[] = percentageOnFiveDropDown;
  public stoneGrades: IDropdown[] = filtersStoneGrades;
  public surfaceFinishing: IDropdown[] = filtersSurfaceFinishing;
  public countryOptions: IDropdown[] = [];

  private _currentLang!: I18n;

  // dependencies
  private readonly _currenciesService = inject(CurrencyService);
  private readonly _storageService = inject(StorageService);
  private readonly _layoutService = inject(LayoutService);
  private readonly _fb = inject(FormBuilder);
  private readonly _router = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _categoriesService = inject(CategoriesService);
  private readonly _countriesService = inject(CountriesService);
  private readonly _translateService = inject(TranslateService);
  private readonly _dialogRef = inject(DialogRef);

  constructor() {
    // set last lang
    this._currentLang = this._translateService.currentLang as keyof II18n;

    // set current currency
    this.currentCurrency =
      this._storageService.getCurrency() as keyof PriceType;
    effect(() => {
      this.currentCurrency = this._currenciesService.currentCurrency();
    });

    this._translateService.onLangChange.subscribe(data => {
      // set current lang
      this._currentLang = data.lang as keyof II18n;
    });

    // hidden advanced filters on route change
    this._router.events.subscribe(val => {
      if (val instanceof NavigationEnd) {
        this.hiddenAdvancedFilters();
      }
    });

    // initialize form
    this.filtersForm = this._fb.group({
      category_type_id: [null],
      category_id: [null],
      sub_category_ids: [null],
      'metadata[stone_grade]': [null],
      origin_country: [null],
      'metadata[trade_market]': [null],
      'metadata[main_color]': [null],
      'metadata[color_percentage]': [null],
      'metadata[veins]': [null],
      'metadata[dots]': [null],
      'metadata[halos]': [null],
      priceRange: {
        lowest_price: [null],
        highest_price: [null],
      },
      lowest_price: [null],
      highest_price: [null],
    });

    // set countries data
    effect(() => {
      this.countryOptions = this._countriesService
        ?.countriesData()
        ?.map(country => ({
          label: country?.name?.[this._currentLang],
          value: country?._id,
        }));
    });

    afterNextRender(() => {
      if (this.countryOptions?.length === 0) {
        this._countriesService.getCountries();
      }
    });

    // set product types
    effect(() => {
      this.setCategories();
      this.setFiltersApplied(this.currentParams);
    });

    this.implementFilters();
  }

  public implementFilters() {
    // get last applied filters
    this._activatedRoute.queryParams.forEach(params => {
      // remove additional filters related to sort
      const filtersFormValues = Object.keys(this.filtersForm.value)
        .map(item => {
          return params[item] ? { [item]: params[item] } : null;
        })
        .filter(item => item);

      this.currentParams = arrayToObject(filtersFormValues);

      const lowest_price = this.currentParams?.['lowest_price']
        ? JSON.parse(this.currentParams?.['lowest_price'])?.[
            this.currentCurrency
          ]
        : null;

      const highest_price = this.currentParams?.['highest_price']
        ? JSON.parse(this.currentParams?.['highest_price'])?.[
            this.currentCurrency
          ]
        : null;

      const currentFilters = {
        lowest_price: 0,
        highest_price: 0,
      };

      if (lowest_price) {
        currentFilters.lowest_price = lowest_price;
      }

      if (highest_price) {
        currentFilters.highest_price = highest_price;
      }

      const priceRange = {
        lowest_price: currentFilters.lowest_price,
        highest_price: currentFilters.highest_price,
      };

      this.filtersForm.patchValue({
        ...this.currentParams,
        ...priceRange,
        priceRange: priceRange,
      });
    });

    this.filtersForm.valueChanges.subscribe(val => {
      this.setFiltersApplied(val);
    });

    // update product types
    this.filtersForm
      .get('category_type_id')
      ?.valueChanges.subscribe((category: string) => {
        // set product types
        this.productTypes =
          this.productCategories?.find(
            (item: CategoriesType) => item?._id === category
          )?.children || [];
        this.dropDownProductTypes = this.productTypes?.map(item => ({
          label: item.title?.[this._currentLang],
          value: item._id,
        }));
      });

    // update product names
    this.filtersForm.get('category_id')?.valueChanges.subscribe(productType => {
      // set product names
      this.productNames =
        this.productTypes?.find(
          (item: CategoriesType) => item?._id === productType
        )?.children || [];
      this.dropDownProductNames = this.productNames?.map(item => ({
        label: item.title?.[this._currentLang],
        value: item._id,
      }));
    });

    // update filter form prices
    this.filtersForm.get('priceRange')?.valueChanges.subscribe(price => {
      this.filtersForm.patchValue({
        lowest_price: price.lowest_price,
        highest_price: price.highest_price,
      });
    });
  }

  public removeAppliedFilter(filter: AppliedFilter) {
    this.filtersApplied = this.filtersApplied.filter(
      item => item.value !== filter.value
    );

    this.filtersForm.patchValue({
      [filter.name]: null,
    });
  }

  public onSubmitFilters() {
    this._router.navigateByUrl('/products');

    const { lowest_price, highest_price, priceRange, origin_country, ...rest } =
      this.filtersForm.value;

    let originCountry: string | string[] | null = origin_country;
    originCountry =
      originCountry && originCountry?.length === 1
        ? originCountry[0]
        : originCountry &&
            originCountry?.length > 1 &&
            typeof originCountry !== 'string'
          ? (originCountry as string[])?.join()
          : typeof originCountry === 'string'
            ? originCountry
            : null;

    const finalFiltersData = removeInvalidValues({
      ...rest,
      origin_country: originCountry,
    });

    // convert multi select output from array to string
    if (finalFiltersData['sub_category_ids']) {
      finalFiltersData['sub_category_ids'] = Array(
        finalFiltersData['sub_category_ids']
      ).join(',');
    }

    let priceFilter;

    if (this.currentCurrency === 'irr') {
      // set form value
      priceFilter = {
        lowest_price: JSON.stringify({
          irr: lowest_price,
        }),
        highest_price: JSON.stringify({
          irr: highest_price,
        }),
      };
    } else if (this.currentCurrency === 'usd') {
      // set form value
      priceFilter = {
        lowest_price: JSON.stringify({
          usd: lowest_price,
        }),
        highest_price: JSON.stringify({
          usd: highest_price,
        }),
      };
    }

    this.onSelectFilter({ ...finalFiltersData, ...priceFilter });
    this.hiddenAdvancedFilters();
  }

  public onSelectFilter(filters: Params) {
    this._router.navigate(['/products'], {
      relativeTo: this._activatedRoute,
      queryParams: { page: null, perPage: null, ...filters },
    });
  }

  public setFiltersApplied(filters: Params) {
    // set filters applied data
    this.filtersApplied = Object.entries(filters)
      .map(entry => {
        // applied filters
        let filter: AppliedFilter = {
          name: '',
          value: '',
        };

        switch (entry[0]) {
          case 'origin_country':
          case 'metadata[trade_market]':
            {
              // trade market & origin countries applied filters
              if (!!entry[1] && entry[1].length > 0) {
                let filtersValue: string[] = entry[1].includes(',')
                  ? entry[1]?.split(',')
                  : entry[1];

                filtersValue =
                  typeof filtersValue === 'string'
                    ? [filtersValue]
                    : filtersValue;

                const selectedCountries = filtersValue.map(
                  (countryId: string) => {
                    const countryLabel = this.countryOptions
                      .filter((country: IDropdown) =>
                        country.value?.includes(countryId)
                      )
                      .map(country => country.label)[0];

                    return countryLabel;
                  }
                );
                // country filter
                filter = {
                  name: entry[0],
                  multiValue: true,
                  value: selectedCountries,
                  translationKey: '',
                };
              }
            }
            break;
          case 'metadata[main_color]':
          case 'metadata[veins]':
          case 'metadata[dots]':
          case 'metadata[halos]':
            {
              if (entry?.[1] && Array.isArray(entry?.[1])) {
                filter = {
                  name: entry[0] ? entry[0] : '',
                  value: entry?.[1],
                  multiValue: true,
                  translationKey: 'enums.colors.',
                };
              }
            }
            break;
          // stone type, form, category
          case 'category_type_id':
            {
              const productCategory = this.dropDownProductCategories?.find(
                item => item.value === entry[1]
              );

              this.productTypes =
                this.productCategories?.find(item => item._id === entry[1])
                  ?.children || [];

              this.dropDownProductTypes = this.productTypes?.map(item => ({
                label: item.title?.[this._currentLang],
                value: item._id,
              }));

              if (productCategory) {
                filter = {
                  name: entry[0] ? entry[0] : '',
                  value: productCategory?.label,
                };
              }
            }
            break;
          case 'category_id':
            {
              const productType = this.dropDownProductTypes?.find(
                item => item.value === entry[1]
              );

              this.productNames =
                this.productTypes?.find(item => item._id === entry[1])
                  ?.children || [];
              this.dropDownProductNames = this.productNames?.map(item => ({
                label: item.title?.[this._currentLang],
                value: item._id,
              }));

              if (productType) {
                filter = {
                  name: entry[0] ? entry[0] : '',
                  value: productType?.label,
                };
              }
            }
            break;
          case 'sub_category_ids':
            {
              if (!!entry[1] && entry[1].length > 0) {
                let filtersValue: string[] = entry[1].includes(',')
                  ? entry[1]?.split(',')
                  : entry[1];

                filtersValue =
                  typeof filtersValue === 'string'
                    ? [filtersValue]
                    : filtersValue;

                const productNames = filtersValue.map((item: string) => {
                  const productNamesData = this.dropDownProductNames
                    .filter((productName: IDropdown) =>
                      productName.value?.includes(item)
                    )
                    .map(item => item.label)[0];

                  return productNamesData;
                });
                // country filter
                filter = {
                  name: entry[0],
                  multiValue: true,
                  value: productNames,
                  translationKey: '',
                };
              }
            }
            break;
          default:
            {
              // other filters
              filter = {
                name: entry[0],
                value: entry[1] ? String(entry[1]) : '',
              };
            }
            break;
        }

        return filter;
      })
      ?.filter(
        (item: AppliedFilter) => item.name !== 'priceRange' && item.value
      );

    // hidden some filters from chips list
    this.filtersChipsList = this.filtersApplied?.filter(
      (item: AppliedFilter) =>
        item.name !== 'lowest_price' && item.name !== 'highest_price'
    );

    this.filtersApplied = this.filtersApplied.filter(
      item => !!item && item.name !== '' && item.value !== ''
    );
  }

  public hiddenAdvancedFilters() {
    // hidden advanced filters
    this.close();
    this._layoutService.hiddenMegaMenu.set(true);
    this._layoutService.hiddenAdvancedFilters.set(true);
    this._layoutService.hiddenAdvancedFilters.set(true);
    this._layoutService.windowIsOpen.set(false);
  }

  public setCategories() {
    // set fetched categories data
    this.productCategories = this._categoriesService.categoriesData();
    // set product categories for dropdown
    this.dropDownProductCategories = this._categoriesService
      .categoriesData()
      ?.map(categoryType => {
        return {
          value: categoryType._id,
          label: getAvailableTranslation(
            categoryType?.title,
            this._currentLang as I18n
          ),
        };
      });
  }

  public close() {
    this._dialogRef.close();
  }
}
